import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaChevronDown } from "react-icons/fa";
import { FiRepeat } from "react-icons/fi";

import { storeLoginTokens } from "../utils/sessionManager";
import { requestPermissionAndSendToken } from "../firebase-config";
import { fetchPartnerContexts, switchPartnerContext } from "./partnerApi";
import {
  applyPartnerSession,
  formatPartnerContextType,
} from "./partnerSession";

function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
}

function isContextActive(ctx, user) {
  if (!ctx || !user) return false;
  if (user.contextId) {
    return String(ctx.id) === String(user.contextId);
  }

  const type = String(ctx.type || "").toUpperCase();
  const userType = String(user.type || "").toUpperCase();
  if (type !== userType) return false;

  if (type === "RST") {
    return String(ctx.restaurantId ?? "") === String(user.restaurantId ?? "");
  }
  if (type === "STR") {
    return String(ctx.storeId ?? "") === String(user.storeId ?? "");
  }
  if (type === "ORG") {
    return String(ctx.organizationId ?? "") === String(user.organizationId ?? "");
  }
  if (type === "EAP") {
    return String(ctx.role || "").toUpperCase() === String(user.role || user.userRole || "").toUpperCase();
  }
  return false;
}

const PartnerBusinessSwitcher = ({ className = "" }) => {
  const [expanded, setExpanded] = useState(false);
  const [contexts, setContexts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [switchingId, setSwitchingId] = useState("");
  const [error, setError] = useState("");

  const user = useMemo(() => getStoredUser(), []);
  const activeContext = useMemo(
    () => contexts.find((ctx) => isContextActive(ctx, user)),
    [contexts, user]
  );
  const activeContextId = activeContext?.id || user?.contextId || "";
  const otherContexts = useMemo(
    () => contexts.filter((ctx) => !isContextActive(ctx, user)),
    [contexts, user]
  );

  const loadContexts = useCallback(async () => {
    if (!localStorage.getItem("Token")) return;
    setLoading(true);
    setError("");
    try {
      const list = await fetchPartnerContexts();
      setContexts(Array.isArray(list) ? list : []);
    } catch {
      setContexts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadContexts();
  }, [loadContexts]);

  const handleToggle = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setExpanded((open) => !open);
  };

  const handleSelectContext = async (contextId) => {
    if (!contextId || contextId === activeContextId || switchingId) return;

    setSwitchingId(contextId);
    setError("");
    try {
      const response = await switchPartnerContext(contextId);
      const sessionUser = response.data?.sessionUser;
      if (!sessionUser) {
        setError(response.data?.message || "Unable to switch business.");
        return;
      }

      storeLoginTokens(response);
      applyPartnerSession(sessionUser);
      requestPermissionAndSendToken().catch(() => {});
      window.location.assign(sessionUser.homePath || "/partner/login");
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Unable to switch business."
      );
    } finally {
      setSwitchingId("");
    }
  };

  if (otherContexts.length === 0) {
    return null;
  }

  return (
    <>
      <div className="maya-account-menu__divider" />
      <div className={`maya-account-menu__switch-section${className ? ` ${className}` : ""}`}>
      <button
        type="button"
        className="maya-account-menu__item maya-account-menu__switch-toggle"
        onClick={handleToggle}
        aria-expanded={expanded}
      >
        <FiRepeat aria-hidden="true" />
        <span>Switch business</span>
        <FaChevronDown
          className={`maya-account-menu__switch-chevron${expanded ? " is-open" : ""}`}
          aria-hidden="true"
        />
      </button>

      {expanded ? (
        <div className="maya-account-menu__entity-list maya-account-menu__switch-list">
          {loading ? (
            <span className="maya-account-menu__switch-status">Loading...</span>
          ) : null}
          {!loading &&
            otherContexts.map((ctx) => (
              <button
                key={ctx.id}
                type="button"
                className="maya-account-menu__item"
                disabled={Boolean(switchingId)}
                onClick={() => handleSelectContext(ctx.id)}
              >
                <span className="maya-account-menu__switch-item-label">
                  <strong>{ctx.businessName || ctx.label}</strong>
                  <small>
                    {formatPartnerContextType(ctx.type)} · {ctx.role}
                  </small>
                </span>
              </button>
            ))}
        </div>
      ) : null}

      {error ? <span className="maya-account-menu__switch-error">{error}</span> : null}
      </div>
    </>
  );
};

export default PartnerBusinessSwitcher;
